import type { ApolloCache, Reference } from '@apollo/client';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import { inflect } from 'inflection';
import * as _ from 'lodash-es';
import React, { useCallback, useLayoutEffect, useRef } from 'react';
import * as styles from './postPermissionOperations.module.scss';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Toast from '../../components/Toast';
import ALL_UNORGANIZED_POSTS_QUERY from '../../graphql/allUnorganizedPosts';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { PostLinkAccess, PostShareRole } from '../../graphql/generated/graphql';
import { UIActions } from '../../ui/uiSlice';
import UserRow from '../../user/UserRow';
import useNavigate from '../../utils/router/useNavigate';
import useConfirmPromise from '../../utils/useConfirmPromise';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import VerifiedIcon from '../VerifiedIcon';

const UPDATE_CACHE_QUERY = gql(/** @complexity 2 */ `
  query updatePostSharingCache($postId: ID!) {
    post(id: $postId) {
      id
      sharing {
        editorIds
        viewerIds
        editorGroupIds
        viewerGroupIds
      }
    }
  }
`);

type UpdatePostSharingCacheQuery = DocumentType<typeof UPDATE_CACHE_QUERY>;
type UpdatePostSharingCacheQueryVariables = VariablesOf<
  typeof UPDATE_CACHE_QUERY
>;

const POST_VIEWERS_QUERY = gql(/** @complexity 3 */ `
  query postPermissionOperationsPostViewers($postId: ID!) {
    post(id: $postId) {
      id
      viewers {
        ...UserRow_user
      }
    }
  }
`);

const UPDATE_POST_LINK_MUTATION = gql(/** @complexity 2 */ `
  mutation UpdatePostLinkAccess($postId: ID!, $linkAccess: PostLinkAccess!) {
    updatePost(id: $postId, linkAccess: $linkAccess) {
      id
      linkAccess
    }
  }
`);

const UPDATE_USER_ROLE_MUTATION = gql(/** @complexity 2 */ `
  mutation UpdateUserInPost($userId: ID!, $postId: ID!, $role: PostShareRole!) {
    user: updateUserInPost(userId: $userId, postId: $postId, role: $role) {
      id
    }
  }
`);

const ADD_USER_MUTATION = gql(/** @complexity 2 */ `
  mutation AddUserToPost($postId: ID!, $userId: ID!) {
    user: addUserToPost(postId: $postId, userId: $userId, role: EDITOR) {
      id
    }
  }
`);

const REMOVE_USER_MUTATION = gql(/** @complexity 2 */ `
  mutation RemoveUserFromPost($postId: ID!, $userId: ID!) {
    user: removeUserFromPost(postId: $postId, userId: $userId) {
      id
    }
  }
`);

const ADD_TOPIC_TO_POST_TOPIC = gql(`
  fragment addTopicToPostTopic on Topic {
    id
  }
`);

const ADD_TOPIC_MUTATION = gql(/** @complexity 2 */ `
  mutation addTopicToPost($postId: ID!, $topicId: ID!) {
    topic: addTopicToPost(postId: $postId, topicId: $topicId) {
      id
      ...addTopicToPostTopic
    }
  }
`);

const REMOVE_TOPIC_MUTATION = gql(/** @complexity 2 */ `
  mutation removeTopicFromPost($postId: ID!, $topicId: ID!) {
    topic: removeTopicFromPost(postId: $postId, topicId: $topicId) {
      id
    }
  }
`);

const ADD_GROUP_MUTATION = gql(/** @complexity 2 */ `
  mutation addGroupToPost($postId: ID!, $groupId: ID!) {
    group: addGroupToPost(postId: $postId, groupId: $groupId, role: EDITOR) {
      id
    }
  }
`);

const REMOVE_GROUP_MUTATION = gql(/** @complexity 2 */ `
  mutation removeGroupFromPost($postId: ID!, $groupId: ID!) {
    group: removeGroupFromPost(postId: $postId, groupId: $groupId) {
      id
    }
  }
`);

const UPDATE_GROUP_ROLE_MUTATION = gql(/** @complexity 2 */ `
  mutation UpdateGroupInPost(
    $groupId: ID!
    $postId: ID!
    $role: PostShareRole!
  ) {
    group: updateGroupInPost(groupId: $groupId, postId: $postId, role: $role) {
      id
    }
  }
`);

const UPDATE_POST_OWNER_MUTATION = gql(/** @complexity 4 */ `
  mutation updatePostOwner($id: ID!, $ownerId: ID!) {
    post: updatePost(id: $id, ownerId: $ownerId) {
      id
      owner {
        id
        name
      }
    }
  }
`);

const addToArray = <T,>(array: T[], id: T) =>
  array.includes(id) ? array : [...array, id];

const updatePostUserRole = <
  P extends Pick<UpdatePostSharingCacheQuery['post'], 'sharing'>,
>(
  post: P,
  userId: string,
  role: PostShareRole | null,
) => {
  const editorIds = post.sharing?.editorIds;
  const viewerIds = post.sharing?.viewerIds;
  if (!editorIds || !viewerIds) return post;

  const newEditorIds =
    role === PostShareRole.Editor
      ? addToArray(editorIds, userId)
      : editorIds.filter((i) => i !== userId);
  const newViewerIds =
    role === PostShareRole.Viewer
      ? addToArray(viewerIds, userId)
      : viewerIds.filter((i) => i !== userId);

  return {
    ...post,
    sharing: {
      ...(post.sharing || {}),
      editorIds: newEditorIds,
      viewerIds: newViewerIds,
    },
  };
};

const updatePostGroupRole = <
  P extends Pick<UpdatePostSharingCacheQuery['post'], 'sharing'>,
>(
  post: P,
  groupId: string,
  role: PostShareRole | null,
) => {
  const editorGroupIds = post.sharing?.editorGroupIds;
  const viewerGroupIds = post.sharing?.viewerGroupIds;
  if (!editorGroupIds || !viewerGroupIds) return post;

  const newEditorGroupIds =
    role === PostShareRole.Editor
      ? addToArray(editorGroupIds, groupId)
      : editorGroupIds.filter((i) => i !== groupId);
  const newViewerGroupIds =
    role === PostShareRole.Viewer
      ? addToArray(viewerGroupIds, groupId)
      : viewerGroupIds.filter((i) => i !== groupId);

  return {
    ...post,
    sharing: {
      ...(post.sharing ?? {}),
      editorGroupIds: newEditorGroupIds,
      viewerGroupIds: newViewerGroupIds,
    },
  };
};

const getUpdater: (
  postId: string,
  role: PostShareRole | null,
  idPath: string,
  type: 'user' | 'group',
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => any =
  (postId: string, role: PostShareRole | null, idPath: string, type) =>
  <T extends UpdatePostSharingCacheQuery>(
    cache: ApolloCache<T>,
    response: object,
  ) => {
    const id = _.get(response, idPath);
    if (!id) return;

    const prev = cache.readQuery<
      UpdatePostSharingCacheQuery,
      UpdatePostSharingCacheQueryVariables
    >({
      query: UPDATE_CACHE_QUERY,
      variables: { postId },
    });

    if (!prev?.post) return;

    const next = {
      ...prev,
      post:
        type === 'user'
          ? updatePostUserRole(prev.post, id, role)
          : updatePostGroupRole(prev.post, id, role),
    };

    cache.writeQuery<
      UpdatePostSharingCacheQuery,
      UpdatePostSharingCacheQueryVariables
    >({
      query: UPDATE_CACHE_QUERY,
      variables: { postId },
      data: next,
    });
  };

const LOSE_EDIT_PERMISSION_TEXT =
  'You will no longer be able to edit this post afterwards.';
const LOSE_VIEW_PERMISSION_TEXT =
  'You will no longer be able to access this post afterwards.';

const USE_CONFIRM_THEN_MUTATE_POST_FRAGMENT = gql(`
  fragment useConfirmThenMutate_post on Post {
    ...usePostPolicyCheck_post
  }
`);

const useConfirmThenMutate = ({
  onClose,
  onBeforeConfirm,
}: { onClose?: () => void; onBeforeConfirm?: () => void } = {}) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const confirmPromise = useConfirmPromise({ onClose });

  const onBeforeConfirmRef = useRef(onBeforeConfirm);
  useLayoutEffect(() => {
    onBeforeConfirmRef.current = onBeforeConfirm;
  }, [onBeforeConfirm]);

  const navigate = useNavigate();
  const postPolicyCheck = usePostPolicyCheck();

  return useCallback(
    async <T,>(
      {
        title,
        postAfter,
        getMessage,
        alwaysShowConfirmation,
      }: {
        title: string;
        postAfter: DocumentType<typeof USE_CONFIRM_THEN_MUTATE_POST_FRAGMENT>;
        getMessage?: (args: {
          canViewAfter: boolean;
          canEditAfter: boolean;
        }) => string | undefined;
        alwaysShowConfirmation?: boolean;
      },
      mutate: () => Promise<T>,
    ) => {
      const canEditAfter = postPolicyCheck(
        PostPolicyAction.EditPost,
        postAfter,
        currentUser,
      );
      const canViewAfter = postPolicyCheck(
        PostPolicyAction.ViewPost,
        postAfter,
        currentUser,
      );

      const shouldBypassConfirmation = canEditAfter && canViewAfter;

      // user currently can edit otherwise they won't be able to call this function
      if (!alwaysShowConfirmation && shouldBypassConfirmation) {
        return mutate();
      }

      const customMessage = getMessage?.({ canViewAfter, canEditAfter });
      const message =
        customMessage ||
        (canViewAfter ? LOSE_EDIT_PERMISSION_TEXT : LOSE_VIEW_PERMISSION_TEXT);

      onBeforeConfirmRef.current?.();
      const result = await confirmPromise(mutate, title, message);

      if (!canViewAfter) {
        navigate('/');
      } else if (!canEditAfter) {
        // Avoid tandem channel complexities
        window.location.reload();
      }

      return result;
    },
    [confirmPromise, currentUser, postPolicyCheck, navigate],
  );
};

const USE_UPDATE_POST_LINK_ACCESS_POST_FRAGMENT = gql(`
  fragment useUpdatePostLinkAccess_post on Post {
    ...usePostPolicyCheck_post
  }
`);

export const useUpdatePostLinkAccess = () => {
  const postPolicyCheck = usePostPolicyCheck();
  const confirmThenMutate = useConfirmThenMutate();
  const confirmPromise = useConfirmPromise();
  const [updateLinkAccessMutation] = useMutation(UPDATE_POST_LINK_MUTATION);
  const { refetch: fetchPostViewers } = useQuery(POST_VIEWERS_QUERY, {
    skip: true,
  });

  return useCallback(
    (
      post: DocumentType<typeof USE_UPDATE_POST_LINK_ACCESS_POST_FRAGMENT>,
      linkAccess: PostLinkAccess,
    ) => {
      const mockPost = { ...post, linkAccess };
      return confirmThenMutate(
        {
          title: 'Restrict link access?',
          postAfter: mockPost,
          getMessage: ({ canViewAfter }) =>
            `You will no longer ${
              canViewAfter ? 'be able to edit' : 'have access to'
            } this post since it is not shared with you directly. Add yourself before changing to retain access.`,
        },
        () =>
          (async () => {
            const confirmation = () =>
              updateLinkAccessMutation({
                variables: { postId: post.id, linkAccess },
              });

            if (post.publishedAt || linkAccess !== PostLinkAccess.Disabled) {
              return confirmation();
            }

            const result = await fetchPostViewers({
              postId: post.id,
            });

            const affectedUsers = result.data?.post.viewers.filter(
              (viewer) =>
                !postPolicyCheck(PostPolicyAction.ViewPost, mockPost, viewer),
            );

            if (affectedUsers.length) {
              return confirmPromise(
                confirmation,
                'Restrict link access?',
                <>
                  <p>
                    The following previous{' '}
                    {inflect('reader', affectedUsers.length)} will lose access.
                    You can selectively share with them after changing to
                    restore access.
                  </p>
                  {affectedUsers.map((user) => (
                    <div key={user.id} className={styles.userRowContainer}>
                      <UserRow user={user} />
                    </div>
                  ))}
                </>,
              );
            }

            return confirmation();
          })(),
      );
    },
    [
      confirmPromise,
      confirmThenMutate,
      fetchPostViewers,
      postPolicyCheck,
      updateLinkAccessMutation,
    ],
  );
};

const USE_UPDATE_POST_USER_POST_FRAGMENT = gql(`
  fragment useUpdatePostUser_post on Post {
    ...useConfirmThenMutate_post
  }
`);

export const useUpdatePostUser = () => {
  const confirmThenMutate = useConfirmThenMutate();
  const [updateUserMutation] = useMutation(UPDATE_USER_ROLE_MUTATION);

  return useCallback(
    (
      post: DocumentType<typeof USE_UPDATE_POST_USER_POST_FRAGMENT>,
      userId: string,
      role: PostShareRole,
    ) =>
      confirmThenMutate(
        {
          title: 'Change your role?',
          postAfter: updatePostUserRole(post, userId, role),
        },
        () =>
          updateUserMutation({
            variables: { postId: post.id, userId, role },
            update: getUpdater(post.id, role, 'data.user.id', 'user'),
          }),
      ),
    [confirmThenMutate, updateUserMutation],
  );
};

const USE_REMOVE_POST_USER_POST_FRAGMENT = gql(`
  fragment useRemovePostUser_post on Post {
    ...useConfirmThenMutate_post
  }
`);

export const useRemovePostUser = () => {
  const confirmThenMutate = useConfirmThenMutate();
  const [removeUserMutation] = useMutation(REMOVE_USER_MUTATION);

  return useCallback(
    (
      post: DocumentType<typeof USE_REMOVE_POST_USER_POST_FRAGMENT>,
      userId: string,
    ) =>
      confirmThenMutate(
        {
          title: 'Remove yourself from the post?',
          postAfter: updatePostUserRole(post, userId, null),
        },
        () =>
          removeUserMutation({
            variables: { postId: post.id, userId },
            update: getUpdater(post.id, null, 'data.user.id', 'user'),
          }),
      ),
    [confirmThenMutate, removeUserMutation],
  );
};

const USE_ADD_POST_USER_POST_FRAGMENT = gql(`
  fragment useAddPostUser_post on Post {
    ...useConfirmThenMutate_post
  }
`);

export const useAddPostUser = () => {
  const [addUserMutation] = useMutation(ADD_USER_MUTATION);

  return useCallback(
    (
      post: DocumentType<typeof USE_ADD_POST_USER_POST_FRAGMENT>,
      userId: string,
    ) =>
      addUserMutation({
        variables: { postId: post.id, userId },
        update: getUpdater(
          post.id,
          PostShareRole.Editor,
          'data.user.id',
          'user',
        ),
      }),
    [addUserMutation],
  );
};

export const useAddTopicToPost = () => {
  const [addTopicToPost] = useMutation(ADD_TOPIC_MUTATION, {
    refetchQueries: [{ query: ALL_UNORGANIZED_POSTS_QUERY }],
  });

  return useCallback(
    (postId: string, topicId: string) =>
      addTopicToPost({
        variables: { topicId, postId },
        optimisticResponse: { topic: { id: topicId, __typename: 'Topic' } },
        update: (cache, { data }) => {
          if (data?.topic) {
            cache.modify({
              id: cache.identify({ __typename: 'Post', id: postId }),
              fields: {
                topics: (
                  existingTopics: readonly Reference[],
                  { readField },
                ) => {
                  const newTopicRef = cache.writeFragment({
                    data: data.topic,
                    fragment: ADD_TOPIC_TO_POST_TOPIC,
                  });

                  if (
                    newTopicRef == null ||
                    existingTopics.some(
                      (ref) => readField('id', ref) === data.topic.id,
                    )
                  ) {
                    return [...existingTopics];
                  }

                  return [...existingTopics, newTopicRef];
                },
              },
            });
          }
        },
      }),
    [addTopicToPost],
  );
};

const USE_REMOVE_TOPIC_FROM_POST_POST_FRAGMENT = gql(`
  fragment useRemoveTopicFromPost_post on Post {
    ...useConfirmThenMutate_post
  }
`);

export const useRemoveTopicFromPost = () => {
  const [removeTopicFromPost] = useMutation(REMOVE_TOPIC_MUTATION, {
    refetchQueries: [{ query: ALL_UNORGANIZED_POSTS_QUERY }],
  });

  const confirmThenMutate = useConfirmThenMutate();

  return useCallback(
    (
      post: DocumentType<typeof USE_REMOVE_TOPIC_FROM_POST_POST_FRAGMENT>,
      topic: { id: string; name: string },
    ) =>
      confirmThenMutate(
        {
          title: `Remove ${topic.name} from this post?`,
          postAfter: {
            ...post,
            topics: post.topics.filter((t) => t.id !== topic.id),
          },
        },
        () =>
          removeTopicFromPost({
            variables: { postId: post.id, topicId: topic.id },
            optimisticResponse: {
              topic: { id: topic.id, __typename: 'Topic' },
            },
            update: (cache, { data }) => {
              if (data?.topic) {
                cache.modify({
                  id: cache.identify({ __typename: 'Post', id: post.id }),
                  fields: {
                    topics: (
                      existingTopics: readonly Reference[],
                      { readField },
                    ) =>
                      existingTopics.filter(
                        (existingTopic) =>
                          readField('id', existingTopic) !== data.topic.id,
                      ),
                  },
                });
              }
            },
          }),
      ),
    [confirmThenMutate, removeTopicFromPost],
  );
};

export const useAddGroupToPost = () => {
  const [addGroupToPost] = useMutation(ADD_GROUP_MUTATION);

  return useCallback(
    (postId: string, groupId: string) =>
      addGroupToPost({
        variables: { groupId, postId },
        update: getUpdater(
          postId,
          PostShareRole.Editor,
          'data.group.id',
          'group',
        ),
      }),
    [addGroupToPost],
  );
};

const USE_REMOVE_GROUP_FROM_POST_POST_FRAGMENT = gql(`
  fragment useRemoveGroupFromPost_post on Post {
    ...useConfirmThenMutate_post
  }
`);

export const useRemoveGroupFromPost = () => {
  const confirmThenMutate = useConfirmThenMutate();
  const [removeGroupFromPost] = useMutation(REMOVE_GROUP_MUTATION);

  return useCallback(
    (
      post: DocumentType<typeof USE_REMOVE_GROUP_FROM_POST_POST_FRAGMENT>,
      group: { id: string; name: string },
    ) =>
      confirmThenMutate(
        {
          title: 'Remove your group from the post?',
          postAfter: updatePostGroupRole(post, group.id, null),
        },
        () =>
          removeGroupFromPost({
            variables: { postId: post.id, groupId: group.id },
            update: getUpdater(post.id, null, 'data.group.id', 'group'),
          }),
      ),
    [confirmThenMutate, removeGroupFromPost],
  );
};

const USE_UPDATE_POST_GROUP_POST_FRAGMENT = gql(`
  fragment useUpdatePostGroup_post on Post {
    ...useConfirmThenMutate_post
  }
`);

export const useUpdatePostGroup = () => {
  const confirmThenMutate = useConfirmThenMutate();
  const [updateGroupRole] = useMutation(UPDATE_GROUP_ROLE_MUTATION);

  return useCallback(
    (
      post: DocumentType<typeof USE_UPDATE_POST_GROUP_POST_FRAGMENT>,
      group: { id: string; name: string },
      role: PostShareRole,
    ) =>
      confirmThenMutate(
        {
          title: 'Change your group role?',
          postAfter: updatePostGroupRole(post, group.id, role),
        },
        () =>
          updateGroupRole({
            variables: { postId: post.id, groupId: group.id, role },
            update: getUpdater(post.id, role, 'data.group.id', 'group'),
          }),
      ),
    [confirmThenMutate, updateGroupRole],
  );
};

const USE_UPDATE_POST_OWNER_POST_FRAGMENT = gql(`
  fragment useUpdatePostOwner_post on Post {
    verifiedAt
    ...useConfirmThenMutate_post
    ...usePostPolicyCheck_post
  }
`);

export const useUpdatePostOwner = (
  useConfirmThenMutateParams: Parameters<typeof useConfirmThenMutate>[0] = {},
) => {
  const postPolicyCheck = usePostPolicyCheck();
  const dispatch = useAppDispatch();
  const [updateOwner] = useMutation(UPDATE_POST_OWNER_MUTATION);
  const confirmThenMutate = useConfirmThenMutate(useConfirmThenMutateParams);
  const currentUser = useAppSelector((state) => state.session.user);

  return useCallback(
    (
      post: DocumentType<typeof USE_UPDATE_POST_OWNER_POST_FRAGMENT>,
      owner: DocumentType<typeof USE_UPDATE_POST_OWNER_POST_FRAGMENT>['owner'],
    ) =>
      confirmThenMutate(
        {
          title: 'Update post owner?',
          postAfter: { ...post, owner },
          alwaysShowConfirmation: true,
          getMessage: ({ canEditAfter, canViewAfter }) => {
            if (canEditAfter) {
              return 'The old owner may lose access to this post afterwards.';
            }
            return canViewAfter
              ? LOSE_EDIT_PERMISSION_TEXT
              : LOSE_VIEW_PERMISSION_TEXT;
          },
        },
        async () => {
          const result = await updateOwner({
            variables: { id: post.id, ownerId: owner.id },
          });
          const prevOwner = post.owner;

          if (post.verifiedAt) {
            const canEdit = postPolicyCheck(
              PostPolicyAction.EditPost,
              { ...post, owner },
              currentUser,
            );
            dispatch(
              UIActions.pushToast(
                ({ onDismiss }: { onDismiss: () => unknown }) => (
                  <Toast
                    icon={<VerifiedIcon />}
                    onDismiss={onDismiss}
                    title="The owner has been changed"
                    message="They're responsible for keeping verified posts up-to-date and reverifying them regularly."
                    {...(canEdit
                      ? {
                          secondaryText: 'Undo',
                          onSecondaryClick: () => {
                            updateOwner({
                              variables: { id: post.id, ownerId: prevOwner.id },
                            });
                            onDismiss();
                          },
                        }
                      : {})}
                  />
                ),
              ),
            );
          }

          return result;
        },
      ),
    [confirmThenMutate, currentUser, dispatch, postPolicyCheck, updateOwner],
  );
};

const POST_SHARE_SET_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription postShareSetSubscription($postId: ID!) {
    postShareSet(postId: $postId) {
      userId
      groupId
      role
    }
  }
`);

export const usePostPermissionSubscription = (postId: string) => {
  useSubscription(POST_SHARE_SET_SUBSCRIPTION, {
    variables: { postId },
    onData: ({ client, data: subscriptionData }) => {
      const postShareSet = subscriptionData.data?.postShareSet;
      if (!postShareSet) return;

      const role = postShareSet.role ?? null;

      if (postShareSet.userId) {
        getUpdater(postId, role, 'userId', 'user')(client, postShareSet);
      }

      if (postShareSet.groupId) {
        getUpdater(postId, role, 'groupId', 'group')(client, postShareSet);
      }
    },
  });
};
